/**
 * Ephemeral persistence for her chat: a conversation stays on screen for 24
 * hours after the last real turn, and disappears once it goes stale — not
 * because the words matter, but because "you already told me this" is a
 * different feeling from finding an old transcript still sitting there. Her
 * actual memory is the server-side extraction on close (`chatTransport.ts`);
 * this is only about the visible transcript, cached the same way
 * `promises.ts` caches its per-user JSON in Dexie meta.
 *
 * `loadChatDay` is where a stale transcript disappears: a stored record
 * whose `updatedAt` is more than 24h old is discarded in favour of a fresh
 * empty one, on read, so nothing needs to run on a timer to make it happen.
 * `updatedAt` only moves forward when a genuinely new turn is added
 * (`persistTurns` in `HerZone.tsx`, or a `close()` with something new below)
 * — merely opening and reading an old conversation does not reset its clock.
 *
 * `makeDayTransport` wraps the real transport so a reopen-then-close with
 * nothing new said does not spend a server-side extraction on a no-op — it
 * tracks how many turns have already been offered for extraction
 * (`closedThrough`) and only calls through when a genuinely new turn of his
 * has been added since.
 */

import { getMeta, setMeta } from '../db/dexie.js'
import { currentProfile } from '../screens/useProfile.js'
import { apiChatTransport, type ChatTransport, type ChatTurn } from './chatTransport.js'

export interface ChatDayRecord {
  /** When a real turn was last added — the clock the 24h window runs from. */
  updatedAt: number
  turns: ChatTurn[]
  /** How many turns had already been sent to the close endpoint. */
  closedThrough: number
  /**
   * When she ended it — her own goodbye, a slammed door, or a spent budget.
   *
   * A conversation she walked out of is over, and the next one starts from
   * nothing. Keeping it would resume the turn counter where she left off,
   * which is how a turn-12 goodbye used to become a goodbye on every single
   * reopen: the prompt kept being told the conversation was already too long.
   */
  endedAt?: number | null
}

/** The close route's schema caps history at 30 turns (`chat.ts`). */
const CLOSE_HISTORY_MAX = 30

/** How long a transcript stays visible after its last real turn. */
const VISIBLE_MS = 24 * 60 * 60 * 1000

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function metaKey(owner: string): string {
  return `glitime.chatDay.${owner}`
}

function emptyRecord(): ChatDayRecord {
  return { updatedAt: Date.now(), turns: [], closedThrough: 0, endedAt: null }
}

/**
 * The cached transcript — or a fresh, empty one, persisted immediately, when
 * there wasn't one yet, the stored one is more than 24h stale, or she ended
 * the last conversation. An ended conversation is not resumed: the extraction
 * already ran on close, so nothing is lost but the scrollback.
 */
export async function loadChatDay(): Promise<ChatDayRecord> {
  const owner = ownerKey()
  const stored = await getMeta<ChatDayRecord | null>(metaKey(owner), null)
  if (stored != null && stored.endedAt == null && Date.now() - stored.updatedAt < VISIBLE_MS) {
    return stored
  }
  const fresh = emptyRecord()
  await setMeta(metaKey(owner), fresh)
  return fresh
}

/** Mark the visible conversation as one she walked out of. */
export async function markChatEnded(): Promise<void> {
  const owner = ownerKey()
  const stored = await getMeta<ChatDayRecord | null>(metaKey(owner), null)
  if (stored == null) return
  await setMeta(metaKey(owner), { ...stored, endedAt: Date.now() })
}

export async function saveChatDay(record: ChatDayRecord): Promise<void> {
  await setMeta(metaKey(ownerKey()), record)
}

/**
 * A transport whose `send` is the base transport's, untouched, and whose
 * `close` only spends a real extraction call when there is something new to
 * extract — at least one of his turns past what the last close already
 * offered. Either way, `closedThrough` moves up to the current history
 * length; `updatedAt` only moves up alongside it when that new turn is what
 * triggered the call, so a no-op reopen/close never extends the 24h window.
 */
export function makeDayTransport(base: ChatTransport = apiChatTransport): ChatTransport {
  return {
    send: (input, history, turn) => base.send(input, history, turn),
    close(history) {
      void loadChatDay().then(async (record) => {
        const freshTurns = history.slice(record.closedThrough)
        const hasNewUserTurn = freshTurns.some((turn) => turn.who === 'user')
        if (hasNewUserTurn) base.close(history.slice(-CLOSE_HISTORY_MAX))
        await saveChatDay({
          ...record,
          closedThrough: history.length,
          updatedAt: hasNewUserTurn ? Date.now() : record.updatedAt,
        })
      })
    },
  }
}
